/**
 * Format helpers for display (e.g. event names in proper case, result formats)
 */

/**
 * Convert a string to proper/title case for display.
 * e.g. "LONG JUMP" -> "Long Jump", "100M" -> "100m", "high jump" -> "High Jump"
 * @param {string} s
 * @returns {string}
 */
export const toProperCase = (s) => {
  if (s == null || typeof s !== 'string') return s;
  const trimmed = s.trim();
  if (!trimmed) return s;
  return trimmed
    .split(/\s+/)
    .map((word) => {
      // Numbers with optional letter suffix (e.g. 100m, 200) – keep numbers, lowercase suffix
      if (/^\d/.test(word)) {
        return word.replace(/([a-zA-Z]+)$/, (_, suffix) => suffix.toLowerCase());
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

/**
 * Whether an event is a field/distance event (number only, no unit) vs time event (hh:mm:ss.00).
 * Uses type from API (Firestore competition_events) when set; else infers from event_code.
 * @param {{ type?: string, event_code?: string, event?: string }} ev
 * @returns {boolean}
 */
export const isFieldEvent = (ev) => {
  if (!ev) return false;
  const type = (ev.type || '').toLowerCase().trim();
  if (type === 'field' || type === 'distance') return true;
  if (type === 'track') return false;
  const code = (ev.event_code || ev.event || '').toLowerCase();
  return (
    /long_jump|triple_jump|high_jump|pole_vault|shot|shot_put|discus|javelin|hammer/.test(code)
  );
};

/**
 * Numeric sort key for events (e.g. 100m -> 100, 1500m -> 1500). Used to order track events smallest to largest.
 * @param {{ event_code?: string, event?: string }} ev
 * @returns {number}
 */
export const eventSortKey = (ev) => {
  if (!ev) return 0;
  const s = String(ev.event_code || ev.event || '').trim();
  const match = s.match(/\d+(?:\.\d+)?/);
  return match ? parseFloat(match[0], 10) : 0;
};

/**
 * Parse stored time string "hh:mm:ss.00" into { hh, mm, ss, ms } for UI inputs.
 * ss = integer seconds, ms = hundredths (00–99).
 * @param {string} value
 * @returns {{ hh: string, mm: string, ss: string, ms: string }}
 */
export const parseTime = (value) => {
  const def = { hh: '', mm: '', ss: '', ms: '' };
  if (value == null || typeof value !== 'string') return def;
  const v = value.trim();
  if (!v) return def;
  const parts = v.split(':');
  let ssPart = '';
  if (parts.length === 1) ssPart = parts[0] || '';
  else if (parts.length === 2) return { hh: '00', mm: parts[0] || '', ...parseSsMs(parts[1]) };
  else ssPart = parts[2] || '';
  const { ss, ms } = parseSsMs(ssPart);
  if (parts.length === 1) return { hh: '00', mm: '00', ss, ms };
  return { hh: parts[0] || '', mm: parts[1] || '', ss, ms };
};

function parseSsMs(ssPart) {
  const s = String(ssPart || '').trim();
  if (!s) return { ss: '', ms: '' };
  if (s.includes('.')) {
    const [whole, dec = ''] = s.split('.');
    // Return ms as-is (no padding) so inputs show "0" not "00" while typing
    const ms = dec.slice(0, 2);
    return { ss: whole || '', ms };
  }
  return { ss: s, ms: '' };
}

/**
 * Build stored time string from UI inputs.
 * @param {{ hh: string, mm: string, ss: string, ms?: string }} parts
 * @param {{ pad?: boolean }} options pad: true (default) = zero-pad to hh:mm:ss.ms; false = no padding (for typing-friendly UI)
 * @returns {string}
 */
export const formatTime = (parts, options = {}) => {
  const pad = options.pad !== false;
  // When pad is false, keep empty parts empty so inputs don't auto-fill with "0"
  const hh = pad ? (String(parts.hh ?? '').trim() || '0') : String(parts.hh ?? '').trim();
  const mm = pad ? (String(parts.mm ?? '').trim() || '0') : String(parts.mm ?? '').trim();
  const ss = pad ? (String(parts.ss ?? '').trim() || '0') : String(parts.ss ?? '').trim();
  let ms = String(parts.ms ?? '').trim().slice(0, 2);
  if (ms.length === 1) ms = pad ? ms.padEnd(2, '0') : ms;
  else if (ms.length === 0) ms = pad ? '00' : '';
  if (pad) {
    const pad2 = (n) => (n.length >= 2 ? n : n.padStart(2, '0'));
    return `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}.${ms}`;
  }
  return `${hh}:${mm}:${ss}.${ms}`;
};

/** Return time string zero-padded for API/storage (e.g. 01:00:10.90). Use for time values only. */
export const formatTimePadded = (value) => {
  if (value == null || String(value).trim() === '') return '';
  const s = String(value).trim();
  if (!s.includes(':')) return s; // field event number, not a time
  const parts = parseTime(s);
  return formatTime(parts, { pad: true });
};

/** Format time for read-only display (e.g. 00:00:10.90). */
export const formatTimeForDisplay = (value) => {
  if (value == null || String(value).trim() === '') return '—';
  const s = String(value).trim();
  if (!s.includes(':')) return s; // field: show number as-is
  return formatTimePadded(s);
};

/**
 * Format result for display: field events get "m" appended (number only stored).
 * @param {string} value
 * @param {{ type?: string, event_code?: string, event?: string }} ev
 * @returns {string}
 */
export const formatResultForDisplay = (value, ev) => {
  if (value == null || value === '') return '';
  const v = String(value).trim();
  if (!v) return '';
  if (isFieldEvent(ev) && /^\d+(\.\d+)?$/.test(v)) return v + 'm';
  return v;
};
